import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
    LayoutDashboard, Users, Activity, BarChart3, FileText, Target, Inbox,
    LogOut, Bell, ChevronDown, Search, Shield as ShieldIcon, UserCog,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LOGO_SRC = "/players-health-logo.png";

const NAV_GROUPS = [
    {
        title: "General",
        items: [
            { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
        ],
    },
    {
        title: "Pipeline",
        items: [
            { to: "/app/brokers", label: "Broker Tracker", icon: Users, testid: "nav-brokers" },
            { to: "/app/leads", label: "Leads", icon: Inbox, testid: "nav-leads" },
            { to: "/app/activity", label: "Activity Log", icon: Activity, testid: "nav-activity" },
            { to: "/app/submissions", label: "Submissions", icon: FileText, testid: "nav-submissions" },
            { to: "/app/weekly-kpis", label: "Weekly KPIs", icon: BarChart3, testid: "nav-weekly-kpis" },
            { to: "/app/go-gets", label: "Go Gets", icon: Target, testid: "nav-go-gets" },
        ],
    },
    {
        title: "Admin",
        adminOnly: true,
        items: [
            { to: "/app/users", label: "Users", icon: ShieldIcon, testid: "nav-users" },
        ],
    },
];

const PAGE_META = {
    "/app": { title: "Dashboard", crumb: "Overview" },
    "/app/brokers": { title: "Broker Tracker", crumb: "Pipeline" },
    "/app/leads": { title: "Leads", crumb: "Pipeline" },
    "/app/activity": { title: "Activity Log", crumb: "Pipeline" },
    "/app/submissions": { title: "Submissions", crumb: "Pipeline" },
    "/app/weekly-kpis": { title: "Weekly KPIs", crumb: "Pipeline" },
    "/app/go-gets": { title: "Go Gets", crumb: "Pipeline" },
    "/app/users": { title: "Users", crumb: "Admin" },
    "/app/profile": { title: "Your profile", crumb: "Account" },
};

export default function Layout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const location = useLocation();
    const meta = PAGE_META[location.pathname] || { title: "Dashboard", crumb: "Overview" };

    const onLogout = async () => {
        await logout();
        nav("/login");
    };

    const initials = (user?.name || user?.email || "U")
        .split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

    return (
        <div className="flex h-screen bg-ph-canvas overflow-hidden">
            {/* Sidebar */}
            <aside
                className="hidden md:flex w-60 flex-col bg-ph-sidebar border-r border-ph-border"
                data-testid="app-sidebar"
            >
                <div className="px-5 py-5 flex items-center gap-2.5 border-b border-ph-border">
                    <img
                        src={LOGO_SRC}
                        alt="Players Health"
                        className="h-7 w-auto select-none"
                        draggable={false}
                    />
                    <div className="leading-tight">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-ph-mutedSoft font-semibold mt-0.5">MGA BD Platform</div>
                    </div>
                </div>

                <nav className="flex-1 px-2 py-3 space-y-5 overflow-y-auto scrollbar-thin">
                    {NAV_GROUPS.filter((g) => !g.adminOnly || user?.role === "admin").map((group) => (
                        <div key={group.title}>
                            <div className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-ph-mutedSoft">
                                {group.title}
                            </div>
                            <div className="space-y-0.5">
                                {group.items.map(({ to, label, icon: Icon, end, testid }) => (
                                    <NavLink
                                        key={to}
                                        to={to}
                                        end={end}
                                        data-testid={testid}
                                        className={({ isActive }) =>
                                            `group flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                                isActive
                                                    ? "bg-ph-primarySoft text-ph-primaryHover border border-blue-100"
                                                    : "text-ph-muted hover:bg-white hover:text-ph-text border border-transparent"
                                            }`
                                        }
                                    >
                                        {({ isActive }) => (
                                            <>
                                                <Icon
                                                    className={`w-4 h-4 ${isActive ? "text-ph-primary" : "text-ph-mutedSoft group-hover:text-ph-text"}`}
                                                    strokeWidth={2}
                                                />
                                                <span>{label}</span>
                                            </>
                                        )}
                                    </NavLink>
                                ))}
                            </div>
                        </div>
                    ))}
                </nav>

                <div className="px-3 py-3 border-t border-ph-border">
                    <div className="rounded-lg bg-white border border-ph-border px-3 py-2.5 flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-ph-primarySoft text-ph-primary flex items-center justify-center text-xs font-semibold">
                            {initials}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-xs font-semibold text-ph-text truncate" data-testid="sidebar-user-name">
                                {user?.name || user?.email}
                            </div>
                            <div className="text-[10px] text-ph-muted truncate">{user?.email}</div>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main column */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Top bar */}
                <header className="h-14 bg-white border-b border-ph-border flex items-center justify-between px-4 lg:px-6 shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="md:hidden flex items-center gap-2">
                            <img src={LOGO_SRC} alt="Players Health" className="h-6 w-auto" draggable={false} />
                        </div>
                        <div className="text-xs text-ph-mutedSoft hidden sm:block">{meta.crumb}</div>
                        <span className="text-ph-mutedSoft hidden sm:block">/</span>
                        <h1 className="text-sm font-semibold text-ph-text" data-testid="page-title">{meta.title}</h1>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            className="hidden md:flex items-center gap-2 h-9 px-3 rounded-md border border-ph-border bg-white hover:bg-ph-canvas text-xs text-ph-muted transition-colors"
                            data-testid="topbar-search"
                        >
                            <Search className="w-3.5 h-3.5" />
                            <span>Quick search</span>
                            <span className="ml-2 text-[10px] text-ph-mutedSoft border border-ph-border rounded px-1 py-0.5">⌘K</span>
                        </button>
                        <button
                            className="w-9 h-9 rounded-md flex items-center justify-center text-ph-muted hover:bg-ph-canvas hover:text-ph-text transition-colors"
                            data-testid="topbar-notifications"
                            aria-label="Notifications"
                        >
                            <Bell className="w-4 h-4" />
                        </button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    className="flex items-center gap-2 pl-1 pr-2 h-9 rounded-md hover:bg-ph-canvas transition-colors"
                                    data-testid="topbar-user-trigger"
                                >
                                    <div className="w-7 h-7 rounded-full bg-ph-primarySoft text-ph-primary flex items-center justify-center text-[11px] font-semibold">
                                        {initials}
                                    </div>
                                    <ChevronDown className="w-3.5 h-3.5 text-ph-mutedSoft" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel className="text-xs">
                                    <div className="font-semibold text-ph-text">{user?.name || "User"}</div>
                                    <div className="text-[11px] text-ph-muted font-normal">{user?.email}</div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => nav("/app/profile")} data-testid="goto-profile" className="cursor-pointer">
                                    <UserCog className="w-3.5 h-3.5 mr-2" /> Your profile
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={onLogout} data-testid="logout-btn" className="text-red-600 focus:text-red-700 focus:bg-red-50 cursor-pointer">
                                    <LogOut className="w-3.5 h-3.5 mr-2" /> Log out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto scrollbar-thin">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
